import React, { useState, useEffect } from 'react';

function AdminPanel() {
  const [modules, setModules] = useState<{id:number, name:string}[]>([]);
  const [newModule, setNewModule] = useState<string>('');
  const [selectedModule, setSelectedModule] = useState<number|null>(null);
  const [pdfFile, setPdfFile] = useState<File|null>(null);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [questions, setQuestions] = useState<{id:number, question:string, answer1:string, answer2:string, answer3:string, answer4:string, correct_answer:number}[]>([]);
  const [newQuestion, setNewQuestion] = useState<string>('');
  const [newAnswer1, setNewAnswer1] = useState<string>('');
  const [newAnswer2, setNewAnswer2] = useState<string>('');
  const [newAnswer3, setNewAnswer3] = useState<string>('');
  const [newAnswer4, setNewAnswer4] = useState<string>('');
  const [correctAnswer, setCorrectAnswer] = useState<number>(1);
  const [users, setUsers] = useState<{id:number, personalnummer:string, name:string, completed:number, completed_at:string|null}[]>([]);

  // Module laden
  useEffect(() => {
    fetchModules();
    fetchQuestions();
    fetchUsers();
  }, []);

  const fetchModules = async () => {
    const res = await fetch('/api/modules');
    const data = await res.json();
    setModules(data);
  };

  const fetchQuestions = async () => {
    const res = await fetch('/api/questions');
    const data = await res.json();
    setQuestions(data);
  };

  const fetchUsers = async () => {
    const res = await fetch('/api/users');
    const data = await res.json();
    setUsers(data);
  };

  const handleCompleteUser = async (userId: number, userName: string) => {
    if (!window.confirm(`Benutzer "${userName}" wirklich komplett freischalten? (Alle Module gelesen + Test bestanden)`)) return;
    try {
      const res = await fetch(`/api/users/${userId}/complete`, { method: 'POST' });
      if (res.ok) {
        fetchUsers();
        alert('Benutzer erfolgreich freigeschaltet!');
      }
    } catch (error) {
      console.error('Fehler beim Freischalten:', error);
      alert('Fehler beim Freischalten des Benutzers');
    }
  };

  const handleResetUser = async (userId: number, userName: string) => {
    if (!window.confirm(`Fortschritt von "${userName}" wirklich komplett zurücksetzen? (Alle PDFs und Test werden zurückgesetzt)`)) return;
    const password = prompt('Admin-Passwort zur Bestätigung eingeben:');
    if (!password) return;
    
    try {
      const res = await fetch(`/api/users/${userId}/reset`, { 
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      
      if (res.ok) {
        fetchUsers();
        alert('Fortschritt erfolgreich zurückgesetzt!');
      } else {
        const data = await res.json();
        alert(data.error || 'Fehler beim Zurücksetzen');
      }
    } catch (error) {
      console.error('Fehler beim Zurücksetzen:', error);
      alert('Fehler beim Zurücksetzen des Fortschritts');
    }
  };

  const handleUnlockForTest = async (userId: number, userName: string) => {
    if (!window.confirm(`"${userName}" für den Test freischalten? (Alle Module werden als gelesen markiert, der Test muss noch gemacht werden)`)) return;
    try {
      const res = await fetch(`/api/users/${userId}/unlock-for-test`, { method: 'POST' });
      if (res.ok) {
        fetchUsers();
        alert('Benutzer erfolgreich für Test freigeschaltet!');
      }
    } catch (error) {
      console.error('Fehler beim Freischalten für Test:', error);
      alert('Fehler beim Freischalten für Test');
    }
  };

  const handleDeleteUser = async (userId: number, userName: string) => {
    if (!window.confirm(`Benutzer "${userName}" wirklich LÖSCHEN? Diese Aktion kann nicht rückgängig gemacht werden!`)) return;
    const password = prompt('Admin-Passwort zur Bestätigung eingeben:');
    if (!password) return;
    
    try {
      const res = await fetch(`/api/users/${userId}`, { 
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      
      if (res.ok) {
        fetchUsers();
        alert('Benutzer erfolgreich gelöscht!');
      } else {
        const data = await res.json();
        alert(data.error || 'Fehler beim Löschen');
      }
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      alert('Fehler beim Löschen des Benutzers');
    }
  };

  // Modul anlegen
  const handleAddModule = async () => {
    if (!newModule) return;
    try {
      console.log('Sende Modul:', newModule);
      const res = await fetch('/api/modules', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newModule })
      });
      const data = await res.json();
      console.log('Antwort:', data);
      if (res.ok) {
        setNewModule('');
        fetchModules();
        alert('Modul erfolgreich angelegt!');
      } else {
        console.error('Fehler:', data);
        alert('Fehler beim Anlegen des Moduls: ' + (data.error || 'Unbekannter Fehler'));
      }
    } catch (error) {
      console.error('Fehler beim Anlegen:', error);
      alert('Netzwerkfehler: ' + (error instanceof Error ? error.message : 'Unbekannter Fehler'));
    }
  };

  // Modul löschen
  const handleDeleteModule = async (id: number) => {
    if (!window.confirm('Modul wirklich löschen? Alle zugehörigen PDFs werden ebenfalls gelöscht.')) return;
    try {
      const res = await fetch(`/api/modules/${id}`, { method: 'DELETE' });
      if (res.ok) {
        fetchModules();
        alert('Modul erfolgreich gelöscht!');
      }
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
    }
  };

  // PDF auswählen
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setPdfFile(e.target.files[0]);
    }
  };

  // PDF hochladen
  const handleUploadPdf = async () => {
    if (!pdfFile || !selectedModule) return;
    const formData = new FormData();
    formData.append('pdf', pdfFile);
    formData.append('module_id', selectedModule.toString());
    const res = await fetch('/api/upload', {
      method: 'POST',
      body: formData
    });
    if (res.ok) {
      setUploadStatus('PDF erfolgreich hochgeladen!');
    } else {
      setUploadStatus('Fehler beim Hochladen.');
    }
    setPdfFile(null);
  };

  // Frage hinzufügen
  const handleAddQuestion = async () => {
    if (!newQuestion || !newAnswer1 || !newAnswer2 || !newAnswer3 || !newAnswer4) {
      alert('Bitte Frage und alle 4 Antworten eingeben!');
      return;
    }
    try {
      const res = await fetch('/api/questions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          question: newQuestion, 
          answer1: newAnswer1,
          answer2: newAnswer2,
          answer3: newAnswer3,
          answer4: newAnswer4,
          correct_answer: correctAnswer 
        })
      });
      if (res.ok) {
        setNewQuestion('');
        setNewAnswer1('');
        setNewAnswer2('');
        setNewAnswer3('');
        setNewAnswer4('');
        setCorrectAnswer(1);
        fetchQuestions();
        alert('Frage erfolgreich hinzugefügt!');
      }
    } catch (error) {
      console.error('Fehler beim Hinzufügen:', error);
    }
  };

  // Frage löschen
  const handleDeleteQuestion = async (id: number) => {
    if (!window.confirm('Frage wirklich löschen?')) return;
    try {
      await fetch(`/api/questions/${id}`, { method: 'DELETE' });
      fetchQuestions();
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
    }
  };

  return (
    <div className="admin-panel">
      <h2>Admin: Module & PDFs verwalten</h2>
      <div className="admin-section">
        <h3>Neues Modul anlegen</h3>
        <input
          type="text"
          placeholder="Neues Modul"
          value={newModule}
          onChange={e => setNewModule(e.target.value)}
        />
        <button onClick={handleAddModule}>Modul anlegen</button>
        
        <div style={{marginTop: 20}}>
          <h4>Vorhandene Module:</h4>
          {modules.length === 0 ? (
            <p>Keine Module vorhanden. Legen Sie ein neues Modul an.</p>
          ) : (
            <ul style={{listStyle: 'none', padding: 0}}>
              {modules.map(m => (
                <li key={m.id} style={{padding: '8px', background: '#f0f0f0', margin: '5px 0', borderRadius: '5px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <span>{m.name} (ID: {m.id})</span>
                  <button onClick={() => handleDeleteModule(m.id)} style={{background: '#ff4444', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '3px', cursor: 'pointer'}}>Löschen</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div className="admin-section">
        <h3>PDF hochladen</h3>
        <select onChange={e => setSelectedModule(Number(e.target.value))} value={selectedModule ?? ''}>
          <option value="" disabled>Modul wählen</option>
          {modules.map(m => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </select>
        <input type="file" accept="application/pdf" onChange={handleFileChange} />
        <button onClick={handleUploadPdf}>PDF hochladen</button>
        <div className="upload-status">{uploadStatus}</div>
      </div>
      
      <div className="admin-section">
        <h3>Testfragen verwalten</h3>
        <div style={{marginBottom: 20}}>
          <input
            type="text"
            placeholder="Frage eingeben"
            value={newQuestion}
            onChange={e => setNewQuestion(e.target.value)}
            style={{marginBottom: 10, width: '100%'}}
          />
          <input
            type="text"
            placeholder="Antwort 1"
            value={newAnswer1}
            onChange={e => setNewAnswer1(e.target.value)}
            style={{marginBottom: 5, width: '100%'}}
          />
          <input
            type="text"
            placeholder="Antwort 2"
            value={newAnswer2}
            onChange={e => setNewAnswer2(e.target.value)}
            style={{marginBottom: 5, width: '100%'}}
          />
          <input
            type="text"
            placeholder="Antwort 3"
            value={newAnswer3}
            onChange={e => setNewAnswer3(e.target.value)}
            style={{marginBottom: 5, width: '100%'}}
          />
          <input
            type="text"
            placeholder="Antwort 4"
            value={newAnswer4}
            onChange={e => setNewAnswer4(e.target.value)}
            style={{marginBottom: 10, width: '100%'}}
          />
          <div style={{marginBottom: 10}}>
            <label style={{marginRight: 10}}>Richtige Antwort:</label>
            <select value={correctAnswer} onChange={e => setCorrectAnswer(Number(e.target.value))}>
              <option value={1}>Antwort 1</option>
              <option value={2}>Antwort 2</option>
              <option value={3}>Antwort 3</option>
              <option value={4}>Antwort 4</option>
            </select>
          </div>
          <button onClick={handleAddQuestion}>Frage hinzufügen</button>
        </div>
        
        <div>
          <h4>Vorhandene Fragen ({questions.length}/10):</h4>
          {questions.length === 0 ? (
            <p>Keine Fragen vorhanden.</p>
          ) : (
            <ul style={{listStyle: 'none', padding: 0}}>
              {questions.map((q, index) => (
                <li key={q.id} style={{padding: '10px', background: '#f0f0f0', margin: '5px 0', borderRadius: '5px'}}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                    <div style={{flex: 1}}>
                      <strong>Frage {index + 1}:</strong> {q.question}<br/>
                      <div style={{marginTop: 5, paddingLeft: 10}}>
                        1. {q.answer1} {q.correct_answer === 1 && '✓'}<br/>
                        2. {q.answer2} {q.correct_answer === 2 && '✓'}<br/>
                        3. {q.answer3} {q.correct_answer === 3 && '✓'}<br/>
                        4. {q.answer4} {q.correct_answer === 4 && '✓'}
                      </div>
                    </div>
                    <button onClick={() => handleDeleteQuestion(q.id)} style={{background: '#ff4444', padding: '5px 10px'}}>Löschen</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      
      <div className="admin-section">
        <h3>Benutzer verwalten</h3>
        <div>
          <h4>Registrierte Benutzer ({users.length}):</h4>
          {users.length === 0 ? (
            <p>Keine Benutzer registriert.</p>
          ) : (
            <ul style={{listStyle: 'none', padding: 0}}>
              {users.map(u => (
                <li key={u.id} style={{padding: '10px', background: '#f0f0f0', margin: '5px 0', borderRadius: '5px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px'}}>
                  <div>
                    <strong>{u.name}</strong> (Personalnummer: {u.personalnummer})
                    {u.completed === 1 && (
                      <span style={{color: 'green', marginLeft: 10}}>
                        ✓ Abgeschlossen {u.completed_at && `am ${new Date(u.completed_at).toLocaleString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}`}
                      </span>
                    )}
                  </div>
                  <div style={{display: 'flex', gap: '8px'}}>
                    {u.completed !== 1 && (
                      <>
                        <button 
                          onClick={() => handleCompleteUser(u.id, u.name)} 
                          style={{background: '#4CAF50', color: 'white', border: 'none', padding: '8px 15px', borderRadius: '3px', cursor: 'pointer'}}
                        >
                          Freischalten
                        </button>
                        <button 
                          onClick={() => handleUnlockForTest(u.id, u.name)} 
                          style={{background: '#2196F3', color: 'white', border: 'none', padding: '8px 15px', borderRadius: '3px', cursor: 'pointer'}}
                        >
                          Für Test freischalten
                        </button>
                      </>
                    )}
                    <button 
                      onClick={() => handleResetUser(u.id, u.name)} 
                      style={{background: '#ffc107', color: '#333', border: 'none', padding: '8px 15px', borderRadius: '3px', cursor: 'pointer'}}
                    >
                      Zurücksetzen
                    </button>
                    <button 
                      onClick={() => handleDeleteUser(u.id, u.name)} 
                      style={{background: '#f44336', color: 'white', border: 'none', padding: '8px 15px', borderRadius: '3px', cursor: 'pointer'}}
                    >
                      Löschen
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminPanel;
